import * as ed25519 from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha2.js';
import bs58 from 'bs58';
import type { OrderlyKeyPair } from '../types/index.ts';
export { recoverAddOrderlyKeySigner, signAddOrderlyKey, signRegistration, signSettlePnl, signWithdraw } from './wallet/eip712.ts';
export {
  signSolanaAddOrderlyKey,
  signSolanaRegistration,
  signSolanaSettlePnl,
  signSolanaWithdraw,
} from './wallet/solana.ts';

// Configure @noble/ed25519 v3 with SHA-512
ed25519.hashes.sha512 = (message: Uint8Array) => sha512(message);

// ── Ed25519 Key Management ──

export function generateKeyPair(): OrderlyKeyPair {
  const secretKey = ed25519.utils.randomSecretKey();
  const publicKey = ed25519.getPublicKey(secretKey);
  return {
    publicKey: bs58.encode(publicKey),
    secretKey,
  };
}

function getKeyPairStorageKey(brokerId: string, accountId: string): string {
  return `orderly_keypair_${brokerId}_${accountId}`;
}

export function saveKeyPair(brokerId: string, accountId: string, keyPair: OrderlyKeyPair): void {
  const data = {
    publicKey: keyPair.publicKey,
    secretKey: Array.from(keyPair.secretKey),
  };
  localStorage.setItem(getKeyPairStorageKey(brokerId, accountId), JSON.stringify(data));
}

export function clearAllKeyPairs(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('orderly_keypair_')) keysToRemove.push(key);
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

export function loadKeyPair(brokerId: string, accountId: string): OrderlyKeyPair | null {
  const raw = localStorage.getItem(getKeyPairStorageKey(brokerId, accountId));
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    return {
      publicKey: data.publicKey,
      secretKey: new Uint8Array(data.secretKey),
    };
  } catch {
    return null;
  }
}

// ── Request Signing (Ed25519) ──

export function signRequest(
  method: string,
  path: string,
  body: string | null,
  secretKey: Uint8Array,
): { timestamp: number; signature: string } {
  const timestamp = Date.now();
  const message = `${timestamp}${method.toUpperCase()}${path}${body ?? ''}`;
  const encoder = new TextEncoder();
  const msgBytes = encoder.encode(message);
  const sigBytes = ed25519.sign(msgBytes, secretKey);
  // Base64 URL-safe encode
  const signature = btoa(String.fromCharCode(...sigBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return { timestamp, signature };
}
